/**
 * Crew templates — chunk 2, item 7.
 *
 * Contract mirror: labourcore-api/src/modules/crewTemplates/.
 *
 * Everything here went through the generic `table()` factory, so nothing had a
 * tenant scope beyond the company id the browser chose to send. The delete in
 * particular — `DELETE … WHERE id = $1` on a table with RLS disabled — would
 * remove another company's template for any caller who knew its id.
 *
 * The company is STILL chosen by the caller. Widening these reads to every
 * company a user belongs to would change which templates they see, which is a
 * product change rather than a safety fix; it is part of the open
 * active-company decision. What the server enforces is that the company is one
 * the caller belongs to.
 */

import { apiGet, apiPost, apiDelete } from "./client";

export type Row = Record<string, unknown>;

export function fetchCrewTemplates(coordinatorCompanyId: string): Promise<Row[]> {
  return apiGet<Row[]>("/api/crew-templates", {
    coordinator_company_id: coordinatorCompanyId,
  });
}

export function fetchEngagedOperatives(coordinatorCompanyId: string): Promise<Row[]> {
  return apiGet<Row[]>("/api/crew-templates/engaged-operatives", {
    coordinator_company_id: coordinatorCompanyId,
  });
}

export interface CreateCrewTemplateBody {
  coordinator_company_id: string;
  name: string;
  description?: string | null;
  operative_ids: string[];
}

/**
 * Create a template. `created_by_user_id` is NOT part of this body — it is the
 * authenticated subject, resolved server-side. The modal used to read the
 * browser's session and send the id, so the database trusted the client.
 */
export function createCrewTemplate(body: CreateCrewTemplateBody): Promise<Row> {
  return apiPost<Row>("/api/crew-templates", body);
}

export function deleteCrewTemplate(templateId: string): Promise<{ deleted: boolean }> {
  return apiDelete<{ deleted: boolean }>(`/api/crew-templates/${templateId}`);
}

/**
 * The operative ids on a template, for the invite fan-out.
 *
 * The invites themselves stay one call each through the existing invite RPC —
 * bundling writes is what Decisions.md #2 rule 3 forbids, and a partial
 * fan-out needs per-operative results rather than one status.
 */
export function fetchCrewTemplateOperatives(templateId: string): Promise<string[]> {
  return apiGet<{ operative_ids: string[] }>(
    `/api/crew-templates/${templateId}/operatives`,
  ).then((r) => r.operative_ids || []);
}
